import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiArrowRight, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { object, string } from "yup";

import Button from "components/Button";
import Input from "components/Input";
import { routes } from "constants/routes";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import useOverrideTokenFromURL from "hooks/useOverrideTokenFromURL";
import AuthLayout from "layouts/Auth";
import { AppDispatch } from "store";
import { loginUser } from "store/auth/actions";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

interface ILoginForm {
  username: string;
  password: string;
}

const loginFormSchema = object({
  username: string()
    .email()
    .typeError("Email is not a valid email")
    .required("This field is required"),
  password: string().required("This field is required")
});

export default function SignIn() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const { state } = useLocation();
  const [params] = useSearchParams();
  const { trackAllEvents } = useAnalytics();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ILoginForm>({
    defaultValues: {
      username: state?.email
    },
    resolver: yupResolver(loginFormSchema)
  });
  const currentYear = dayjs().year();

  useOverrideTokenFromURL();

  const onSubmit = async ({ username, password }: ILoginForm) => {
    try {
      await dispatch(
        loginUser({
          username: username.toLowerCase(),
          password,
          grant_type: "password",
          client_id: "yzxsFZJYZMxi5kLh21ZZklGYzSuu9zxNO6miNcN8"
        })
      ).unwrap();

      if (params.get("redirection_url") === "join") {
        navigate(routes.HAULPAY_JOIN);
        trackAllEvents("auth_signin_login_redirect");
        trackAllEvents("auth_signin_login");
      } else if (params.get("next")) {
        const url = params.get("next") ?? routes.HOME;
        navigate(url);
        trackAllEvents("auth_signin_login_next");
        trackAllEvents("auth_signin_login");
      } else {
        navigate(routes.HOME);
        trackAllEvents("auth_signin_login");
      }
    } catch {
      showErrorMessage("Login Credentials Incorrect.");
    }
  };

  const handleForgotPassword = () => {
    navigate(routes.RESET_PASSWORD);
  };

  return (
    <AuthLayout>
      <form className={styles.signinForm} onSubmit={handleSubmit(onSubmit)}>
        <Input
          name="username"
          placeholder="Email"
          inputClass="mobile:!rounded-b-none lowercase"
          labelClass="desktop:!text-base desktop:!font-medium"
          register={register}
          error={errors.username}
          data-cy="login-username-input"
          type="email"
          {...(isDesktop && { label: "Email" })}
        />
        <Input
          name="password"
          placeholder="Password"
          inputClass="mobile:!rounded-t-none"
          labelClass="desktop:!text-base desktop:!font-medium"
          register={register}
          error={errors.password}
          data-cy="login-password-input"
          {...(isDesktop && { label: "Password" })}
          isPasswordIcon
        />

        <Link to={routes.RESET_PASSWORD} className={styles.forgotLink}>
          Forgot Password?
        </Link>

        <div className={styles.signinFooter}>
          <div className="flex gap-5">
            <Button
              type="submit"
              size="lg"
              suffixNode={
                !isDesktop && (
                  <span className={styles.arrowRightIcon}>
                    <Icon path={mdiArrowRight} className="m-auto text-white/50" />
                  </span>
                )
              }
              className="desktop:!px-8 mobile:!w-full mobile:!bg-navActive"
              data-cy="login-submit-button"
              disabled={isSubmitting}>
              {isSubmitting ? (
                <Icon path={mdiLoading} size={1.5} spin={1} />
              ) : isDesktop ? (
                "Login"
              ) : (
                "LOGIN"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="lg"
              className="!px-8 mobile:hidden"
              onClick={handleForgotPassword}>
              Forgot Password
            </Button>
          </div>

          <span className={styles.signupLink}>
            <Link {...(isDesktop ? { to: `${routes.SIGNUP}` } : { to: "" })} data-cy="signup-link">
              {!isDesktop ? "Don’t have an account? " : "Need to Signup? "}
            </Link>
            <Link to={routes.SIGNUP} className="mobile:underline" data-cy="signup-link">
              {!isDesktop ? "Signup" : "Click here"}
            </Link>
          </span>

          <p className="mx-auto mt-auto text-xs font-light text-subtitle desktop:hidden">
            &copy; {currentYear} HaulPay | All Rights Reserved
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
